import { destroyRegisteredControlsIn } from "./controls/controlCore.js";
import { bindNativeFieldBehavior } from "../forms/nativeFieldStyles.js";

const confirmModalElement = document.getElementById('confirm-modal');
let activeConfirmationCleanup = null;
let confirmationFocusIntentToken = 0;
const FOCUS_NAVIGATION_KEYS = new Set([
    'Tab',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageUp',
    'PageDown',
]);
const BUTTON_VARIANT_CLASSES = {
    danger: [
        'inline-flex',
        'h-9',
        'items-center',
        'justify-center',
        'whitespace-nowrap',
        'rounded-md',
        'bg-red-600',
        'px-4',
        'text-sm',
        'font-semibold',
        'text-white',
        'transition-colors',
        'hover:bg-red-700',
        'focus:outline-none',
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
    ],
    primary: [
        'inline-flex',
        'h-9',
        'min-w-[88px]',
        'items-center',
        'justify-center',
        'whitespace-nowrap',
        'rounded-md',
        'bg-[var(--dfc-brand-primary)]',
        'px-4',
        'text-sm',
        'font-semibold',
        'text-[var(--dfc-brand-primary-contrast)]',
        'transition-colors',
        'hover:bg-[var(--dfc-brand-primary-hover)]',
        'focus:outline-none',
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
    ],
    secondary: [
        'inline-flex',
        'h-9',
        'items-center',
        'justify-center',
        'whitespace-nowrap',
        'rounded-md',
        'bg-gray-200',
        'px-4',
        'text-sm',
        'font-semibold',
        'text-gray-800',
        'transition-colors',
        'hover:bg-gray-300',
        'focus:outline-none',
    ],
    dangerSecondary: [
        'inline-flex',
        'h-9',
        'items-center',
        'justify-center',
        'whitespace-nowrap',
        'rounded-md',
        'border',
        'border-red-200',
        'bg-white',
        'px-4',
        'text-sm',
        'font-semibold',
        'text-red-700',
        'transition-colors',
        'hover:border-red-300',
        'hover:bg-red-50',
        'focus:outline-none',
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
    ],
};

const ALL_BUTTON_VARIANT_CLASSES = Object.values(BUTTON_VARIANT_CLASSES).flat();
const MODAL_WIDTH_CLASSES = [
    'max-w-sm',
    'max-w-2xl',
    'max-w-3xl',
];
const MODAL_SCROLL_CLASSES = [
    'max-h-[calc(100vh-2rem)]',
    'flex',
    'flex-col',
];
const MESSAGE_SCROLL_CLASSES = [
    'min-h-0',
    'overflow-y-auto',
    'pr-1',
];
const TITLE_SIZE_OVERRIDE_CLASSES = [
    'text-xl',
    'leading-snug',
    'sm:text-2xl',
];
const MODAL_INSET_CLASSES = [
    'px-1',
];

function applyButtonVariant(button, variant) {
    if (!button) {return;}
    button.classList.remove(...ALL_BUTTON_VARIANT_CLASSES);
    button.classList.add(...BUTTON_VARIANT_CLASSES[variant]);
}

function applyModalWidth(inner, width = 'sm') {
    if (!inner) {return;}
    const className = width === '3xl' ? 'max-w-3xl' :
        width === '2xl' ? 'max-w-2xl' :
        'max-w-sm';
    inner.classList.remove(...MODAL_WIDTH_CLASSES);
    inner.classList.add(className);
}

function getConfirmationTabStops() {
    if (!confirmModalElement) {return [];}
    return Array.from(confirmModalElement.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), ' +
        'select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.getClientRects().length > 0 &&
        element.getAttribute('aria-hidden') !== 'true');
}

function resolveFocusTarget(target) {
    try {
        const resolved = typeof target === 'function' ? target() : target;
        return resolved instanceof HTMLElement ? resolved : null;
    } catch (error) {
        console.warn('Could not resolve confirmation return-focus target:', error);
        return null;
    }
}

function canReceiveRestoredFocus(target) {
    return Boolean(target?.isConnected &&
        target.getClientRects().length > 0 &&
        !target.closest('[aria-hidden="true"]') &&
        !target.closest('[inert]'));
}

function concealUnderlyingFormModal() {
    const formModal = document.getElementById('form-modal');
    if (!formModal?.classList.contains('flex')) {return () => {};}
    const previousAriaHidden = formModal.getAttribute('aria-hidden');
    const previouslyInert = formModal.hasAttribute('inert');
    formModal.setAttribute('aria-hidden', 'true');
    formModal.setAttribute('inert', '');
    return () => {
        if (!formModal.classList.contains('flex')) {
            formModal.setAttribute('aria-hidden', 'true');
        } else if (previousAriaHidden === null) {
            formModal.removeAttribute('aria-hidden');
        } else {
            formModal.setAttribute('aria-hidden', previousAriaHidden);
        }
        if (previouslyInert) {
            formModal.setAttribute('inert', '');
        } else {
            formModal.removeAttribute('inert');
        }
    };
}

function invalidateConfirmationFocusIntent() {
    confirmationFocusIntentToken += 1;
}

function confirmationStillOwnsFocus(closingFocus) {
    const active = document.activeElement;
    return active === document.body || active === closingFocus ||
        Boolean(confirmModalElement?.contains(active));
}

document.addEventListener('pointerdown', invalidateConfirmationFocusIntent, true);
document.addEventListener('focusin', invalidateConfirmationFocusIntent, true);
document.addEventListener('keydown', (event) => {
    if (FOCUS_NAVIGATION_KEYS.has(event.key)) {
        invalidateConfirmationFocusIntent();
    }
}, true);

/**
 * Opens the app-owned confirmation modal.
 *
 * @param {string} title - The text for the modal's title.
 * @param {string} message - The HTML/text for the modal's body.
 * @param {Function} onConfirmCallback - The async function to run on confirm.
 * @param {boolean} [danger=true] - Whether to style the confirm action as destructive.
 * @param {string} [buttonText='Delete'] - The confirm button label.
 * @param {Function|null} [onCancelCallback=null] - Optional callback for cancellation.
 * @param {string} [loadingText=''] - Optional confirm button label while running.
 * @param {string} [cancelButtonText='Cancel'] - The cancel button label.
 * @param {object} [options={}] Optional advanced modal actions.
 * @param {object|null} [options.leftAction=null] Optional left-aligned action.
 * @param {string} [options.leftAction.placement='left'] Optional placement. Use `beforeConfirm`
 *     to place the extra action between Cancel and Confirm.
 * @param {string} [options.leftAction.variant] Optional button variant.
 * @param {boolean} [options.closeOnError=true] Whether to close after action errors.
 * @param {boolean} [options.keepOpenOnFalse=false] Keep the modal open when the confirm callback returns false.
 * @param {string} [options.maxWidth='sm'] Modal max-width token (`sm`, `2xl`, or `3xl`).
 * @param {string} [options.contentInset=''] Optional inset class for title/actions.
 * @param {string} [options.actionsLayout='split'] Action layout token (`split` or `right`).
 * @param {boolean} [options.scrollBody=false] Keep modal actions visible with an internal scrolling body.
 * @param {boolean} [options.hideCancel=false] Hide the cancel button for acknowledgement modals.
 * @param {HTMLElement|Function|null} [options.initialFocusTarget=null] Optional initial focus target.
 * @param {HTMLElement|Function|null} [options.returnFocusTarget=null] Optional target, or target
 *     resolver, used when the control focused before opening is no longer the right return point.
 * @returns {void}
 */
export function openConfirmationModal(
    title,
    message,
    onConfirmCallback,
    danger = true,
    buttonText = 'Delete',
    onCancelCallback = null,
    loadingText = '',
    cancelButtonText = 'Cancel',
    options = {},
) {
    if (!confirmModalElement) {
        console.error("Confirmation modal element not found.");
        return;
    }

    activeConfirmationCleanup?.({restoreFocus: false});
    const previouslyFocused = document.activeElement instanceof HTMLElement ?
        document.activeElement : null;
    const restoreUnderlyingFormModal = concealUnderlyingFormModal();

    const titleElement = confirmModalElement.querySelector('#confirm-title');
    const messageElement = confirmModalElement.querySelector('#confirm-message');
    const confirmBtn = confirmModalElement.querySelector('#confirm-delete-btn');
    const leftActionBtn = confirmModalElement.querySelector('#confirm-left-action-btn');
    const leftActionHome = leftActionBtn?.parentElement || null;
    const cancelBtn = confirmModalElement.querySelector('#cancel-confirm-btn');
    const actionsRow = cancelBtn?.parentElement || null;
    const actionGroup = confirmBtn?.parentElement || null;

    if (!titleElement || !messageElement || !confirmBtn) {
        restoreUnderlyingFormModal();
        console.error("Required elements missing within #confirm-modal.");
        return;
    }

    const inner = confirmModalElement.querySelector('div');
    applyModalWidth(inner, options.maxWidth || 'sm');
    if (options.scrollBody === true) {
        inner?.classList.add(...MODAL_SCROLL_CLASSES);
        messageElement.classList.add(...MESSAGE_SCROLL_CLASSES);
    } else {
        inner?.classList.remove(...MODAL_SCROLL_CLASSES);
        messageElement.classList.remove(...MESSAGE_SCROLL_CLASSES);
    }

    titleElement.classList.remove(...TITLE_SIZE_OVERRIDE_CLASSES);
    titleElement.classList.remove(...MODAL_INSET_CLASSES);
    titleElement.classList.add('text-lg');
    titleElement.textContent = title;
    if (MODAL_INSET_CLASSES.includes(options.contentInset)) {
        titleElement.classList.add(options.contentInset);
    }
    destroyRegisteredControlsIn(messageElement);
    messageElement.innerHTML = message;
    bindNativeFieldBehavior(messageElement);
    confirmBtn.textContent = buttonText;
    applyButtonVariant(confirmBtn, danger ? 'danger' : 'primary');
    confirmBtn.disabled = false;
    actionsRow?.classList.remove(...MODAL_INSET_CLASSES);
    if (MODAL_INSET_CLASSES.includes(options.contentInset)) {
        actionsRow?.classList.add(options.contentInset);
    }
    if (options.actionsLayout === 'right') {
        actionsRow?.classList.remove('justify-between');
        actionsRow?.classList.add('justify-end');
        actionGroup?.classList.remove('ml-auto');
    } else {
        actionsRow?.classList.remove('justify-end');
        actionsRow?.classList.add('justify-between');
        actionGroup?.classList.add('ml-auto');
    }

    let closed = false;
    let keydownHandler = null;
    const cleanup = ({restoreFocus = true} = {}) => {
        if (closed) {return;}
        const closingFocus = document.activeElement;
        const overlayOwnedFocus = closingFocus === document.body ||
            confirmModalElement.contains(closingFocus);
        closed = true;
        if (activeConfirmationCleanup === cleanup) {
            activeConfirmationCleanup = null;
        }
        if (keydownHandler) {
            document.removeEventListener('keydown', keydownHandler, true);
            keydownHandler = null;
        }
        destroyRegisteredControlsIn(messageElement);
        confirmModalElement.classList.remove('flex');
        confirmModalElement.setAttribute('aria-hidden', 'true');
        restoreUnderlyingFormModal();
        confirmBtn.disabled = false;
        confirmBtn.onclick = null;
        confirmBtn.textContent = buttonText;

        if (leftActionBtn) {
            leftActionBtn.classList.add('hidden');
            leftActionBtn.classList.remove('inline-flex');
            leftActionBtn.onclick = null;
            leftActionBtn.disabled = false;
            if (leftActionHome && leftActionBtn.parentElement !== leftActionHome) {
                leftActionHome.prepend(leftActionBtn);
            }
        }

        if (cancelBtn) {
            cancelBtn.classList.remove('hidden');
            cancelBtn.classList.add('inline-flex');
            cancelBtn.textContent = 'Cancel';
            applyButtonVariant(cancelBtn, 'secondary');
            cancelBtn.onclick = null;
        }

        confirmModalElement.onclick = null;

        applyModalWidth(inner, 'sm');
        inner?.classList.remove(...MODAL_SCROLL_CLASSES);
        messageElement.classList.remove(...MESSAGE_SCROLL_CLASSES);
        titleElement.classList.remove(...TITLE_SIZE_OVERRIDE_CLASSES);
        titleElement.classList.remove(...MODAL_INSET_CLASSES);
        titleElement.classList.add('text-lg');
        actionsRow?.classList.remove(...MODAL_INSET_CLASSES, 'justify-end');
        actionsRow?.classList.add('justify-between');
        actionGroup?.classList.add('ml-auto');

        if (restoreFocus && overlayOwnedFocus) {
            const focusToken = ++confirmationFocusIntentToken;
            requestAnimationFrame(() => {
                if (focusToken !== confirmationFocusIntentToken ||
                    !confirmationStillOwnsFocus(closingFocus) ||
                    confirmModalElement.classList.contains('flex')) {return;}
                const explicitTarget = resolveFocusTarget(options.returnFocusTarget);
                const target = canReceiveRestoredFocus(explicitTarget) ?
                    explicitTarget : previouslyFocused;
                if (canReceiveRestoredFocus(target)) {
                    target.focus({preventScroll: true});
                }
            });
        }
    };
    activeConfirmationCleanup = cleanup;

    confirmBtn.onclick = async () => {
        try {
            confirmBtn.disabled = true;
            if (loadingText) {
                confirmBtn.textContent = loadingText;
            }
            const result = await onConfirmCallback();
            if (result === false && options.keepOpenOnFalse === true) {
                confirmBtn.disabled = false;
                confirmBtn.textContent = buttonText;
                return;
            }
            cleanup();
        } catch (err) {
            console.error("Error during confirmation action:", err);
            if (options.closeOnError !== false) {
                confirmBtn.disabled = false;
                confirmBtn.textContent = buttonText;
                cleanup();
            }
        }
    };

    if (cancelBtn && options.hideCancel === true) {
        cancelBtn.classList.add('hidden');
        cancelBtn.classList.remove('inline-flex');
        cancelBtn.onclick = null;
    } else if (cancelBtn) {
        cancelBtn.classList.remove('hidden');
        cancelBtn.classList.add('inline-flex');
        cancelBtn.textContent = cancelButtonText;
        applyButtonVariant(cancelBtn, 'secondary');
        cancelBtn.onclick = () => {
            cleanup();
            if (onCancelCallback) {
                onCancelCallback();
            }
        };
    }

    if (leftActionBtn && options.leftAction) {
        const leftAction = options.leftAction;
        leftActionBtn.textContent = leftAction.buttonText || 'Continue';
        applyButtonVariant(
            leftActionBtn,
            leftAction.variant || (leftAction.danger === false ? 'secondary' : 'danger'),
        );
        leftActionBtn.classList.remove('hidden');
        leftActionBtn.classList.add('inline-flex');
        leftActionBtn.disabled = false;
        if (leftAction.placement === 'beforeConfirm') {
            confirmBtn.parentElement?.insertBefore(leftActionBtn, confirmBtn);
        }
        leftActionBtn.onclick = async () => {
            try {
                leftActionBtn.disabled = true;
                if (leftAction.loadingText) {
                    leftActionBtn.textContent = leftAction.loadingText;
                }
                await leftAction.onClick();
                cleanup();
            } catch (err) {
                console.error("Error during left confirmation action:", err);
                if (options.closeOnError !== false) {
                    leftActionBtn.disabled = false;
                    leftActionBtn.textContent = leftAction.buttonText || 'Continue';
                    cleanup();
                }
            }
        };
    } else if (leftActionBtn) {
        leftActionBtn.classList.add('hidden');
        leftActionBtn.classList.remove('inline-flex');
        leftActionBtn.onclick = null;
        leftActionBtn.disabled = false;
    }

    confirmModalElement.setAttribute('role', 'dialog');
    confirmModalElement.setAttribute('aria-modal', 'true');
    confirmModalElement.setAttribute('aria-labelledby', 'confirm-title');
    confirmModalElement.setAttribute('aria-describedby', 'confirm-message');
    confirmModalElement.setAttribute('aria-hidden', 'false');
    confirmModalElement.onclick = null;
    confirmModalElement.classList.add('flex');
    keydownHandler = (event) => {
        if (!confirmModalElement.classList.contains('flex')) {return;}
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            if (cancelBtn && !cancelBtn.classList.contains('hidden')) {
                cancelBtn.click();
            } else {
                cleanup();
                if (typeof onCancelCallback === 'function') {
                    onCancelCallback();
                }
            }
            return;
        }
        if (event.key !== 'Tab') {return;}
        const stops = getConfirmationTabStops();
        event.stopPropagation();
        if (!stops.length) {
            event.preventDefault();
            return;
        }
        const first = stops[0];
        const last = stops.at(-1);
        if (event.shiftKey &&
            (document.activeElement === first ||
                !confirmModalElement.contains(document.activeElement))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey &&
            (document.activeElement === last ||
                !confirmModalElement.contains(document.activeElement))) {
            event.preventDefault();
            first.focus();
        }
    };
    document.addEventListener('keydown', keydownHandler, true);
    const initialFocusOwner = document.activeElement;
    const initialFocusToken = ++confirmationFocusIntentToken;
    requestAnimationFrame(() => {
        if (initialFocusToken !== confirmationFocusIntentToken ||
            !confirmModalElement.classList.contains('flex') || closed) {return;}
        const active = document.activeElement;
        if (active !== document.body && active !== initialFocusOwner &&
            !confirmModalElement.contains(active)) {return;}
        const requested = resolveFocusTarget(options.initialFocusTarget);
        const fallback = cancelBtn && !cancelBtn.classList.contains('hidden') ?
            cancelBtn : confirmBtn;
        (canReceiveRestoredFocus(requested) ? requested : fallback)?.focus();
    });
}
